import { types, flow, applySnapshot, getSnapshot } from "mobx-state-tree";
import { PUBLISH_STATUSES } from "constants/models";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";
import { MSURL } from "utils/ms";
import { isEmptyObject } from "../utils";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const corePath = "/core/api/v1";
export const TargetZoneModel = types
  .model({
    id: types.number,
    title: types.string,
    image: types.maybeNull(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.image,
      };
    },
  }));

const TargetZoneStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${corePath}/target-zone`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: TargetZoneModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(TargetZoneModel),
    isMoreLoading: types.optional(types.boolean, false),
    isTargetZoneLoading: types.optional(types.boolean, false),
    isTargetZoneManaging: types.optional(types.boolean, false),
    isTargetZonePublishing: types.optional(types.boolean, false),
    isTargetZoneCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateTargetZone(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isTargetZoneLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${corePath}/target-zone`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZoneLoading = false;
      }
    }),

    fetchDetails: flow(function*(id?: number) {
      self.isTargetZoneLoading = true;

      try {
        const details = yield api.get(
          `${corePath}/target-zone/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZoneLoading = false;
      }
    }),

    create: flow(function*(payload: any) {
      self.isTargetZoneManaging = true;

      try {
        const { id } = yield api.post(
          `${corePath}/target-zone`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZoneManaging = false;
      }
    }),

    editInfo: flow(function*(payload: any) {
      self.isTargetZoneManaging = true;

      try {
        return yield api.put(
          `${corePath}/target-zone/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZoneManaging = false;
      }
    }),

    uploadImage: flow(function*(payload: any) {
      self.isTargetZoneManaging = true;

      try {
        return yield api.putFormData(
          `${corePath}/target-zone/${payload.id}/upload-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZoneManaging = false;
      }
    }),

    changeStatus: flow(function*(id: number, status: string) {
      self.isTargetZonePublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${corePath}/target-zone/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateTargetZone(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTargetZonePublishing = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default TargetZoneStore.create();
